import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { z } from "zod";
import { type GovernanceUnitNode } from "~/api/types";
import { createGovernanceUnit } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import { Input } from "~/atoms/input";
import { homeGovernanceUnit } from "~/helpers/governanceUnit";
import { governanceUnitNameSchema } from "~/helpers/governanceUnitName";
import { GovernanceUnitSelect } from "~/molecules/GovernanceUnitSelect";

const FormSchema = z.object({
  name: governanceUnitNameSchema,
  parentGovernanceUnitId: z.number(),
});

type FormValues = z.infer<typeof FormSchema>;

interface Props {
  governanceUnits: GovernanceUnitNode[];
  open: boolean;
  onClose: () => void;
  onCreated: (parentGovernanceUnitId: number) => void;
}

export function CreateGovernanceUnitDialog({
  governanceUnits,
  open,
  onClose,
  onCreated,
}: Props) {
  const { t } = useTranslation();
  const home = homeGovernanceUnit(governanceUnits);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(($) => $.governanceUnits.createDialog.title)}
          </DialogTitle>
        </DialogHeader>
        {home && (
          <CreateForm
            home={home}
            onCreated={(parentGovernanceUnitId) => {
              onCreated(parentGovernanceUnitId);
              onClose();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface CreateFormProps {
  home: GovernanceUnitNode;
  onCreated: (parentGovernanceUnitId: number) => void;
}

function CreateForm({ home, onCreated }: CreateFormProps) {
  const { t } = useTranslation();
  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      parentGovernanceUnitId: home.id,
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await createGovernanceUnit(data);
      toast.success(t(($) => $.governanceUnits.createSuccess));
      onCreated(data.parentGovernanceUnitId);
    } catch {
      toast.error(t(($) => $.governanceUnits.createError));
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t(($) => $.governanceUnits.name)}</FormLabel>
              <FormControl>
                <Input autoFocus {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="parentGovernanceUnitId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(($) => $.governanceUnits.createDialog.parentLabel)}
              </FormLabel>
              <FormControl>
                <GovernanceUnitSelect
                  root={home}
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {t(($) => $.commons.buttons.create)}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
